<script setup>
import { ref, watch, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { onClickOutside, useElementBounding } from '@vueuse/core';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Icon from 'next/icon/Icon.vue';
import { useAddAttachmentToTicket } from 'dashboard/composables/useAddAttachmentToTicket';

const props = defineProps({
  attachmentId: {
    type: Number,
    required: true,
  },
  show: {
    type: Boolean,
    default: false,
  },
  triggerRef: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(['close', 'linked']);

const { t } = useI18n();
const dropdownRef = ref(null);

// Dynamic positioning based on trigger element
const triggerElement = computed(() => props.triggerRef?.$el || props.triggerRef);
const { bottom, right } = useElementBounding(triggerElement);

const dropdownStyle = computed(() => {
  if (!props.triggerRef || !bottom.value) return {};
  return {
    position: 'fixed',
    top: `${bottom.value + 4}px`,
    right: `${window.innerWidth - right.value}px`,
    zIndex: 9999,
  };
});

const {
  tickets,
  isLoadingTickets,
  isLinking,
  fetchContactTickets,
  linkAttachmentToTicket,
} = useAddAttachmentToTicket();

onClickOutside(dropdownRef, () => {
  emit('close');
});

const handleTicketSelect = async ticket => {
  const success = await linkAttachmentToTicket(
    ticket.display_id,
    props.attachmentId
  );
  if (success) {
    emit('linked', ticket);
    emit('close');
  }
};

watch(
  () => props.show,
  newVal => {
    if (newVal) {
      fetchContactTickets();
    }
  },
  { immediate: true }
);

const getStatusColor = status => {
  const colors = {
    open: 'bg-n-amber-9/20 text-n-amber-11',
    in_progress: 'bg-n-brand/20 text-n-blue-text',
    escalated: 'bg-n-ruby-9/20 text-n-ruby-11',
    waiting_client: 'bg-orange-100 text-orange-800',
    waiting_internal: 'bg-purple-100 text-purple-800',
  };
  return colors[status] || 'bg-n-slate-9/20 text-n-slate-11';
};
</script>

<template>
  <Teleport to="body">
    <div
      v-if="show"
      ref="dropdownRef"
      :style="dropdownStyle"
      class="min-w-56 max-w-72 bg-n-solid-3 border border-n-weak rounded-lg shadow-lg overflow-hidden"
    >
    <!-- Header -->
    <div class="px-3 py-2 border-b border-n-weak bg-n-solid-2">
      <span class="text-sm font-medium text-n-slate-12">
        {{ $t('TICKETS.ATTACHMENTS.SELECT_TICKET') }}
      </span>
    </div>

    <!-- Loading -->
    <div v-if="isLoadingTickets" class="flex justify-center py-4">
      <Spinner size="small" />
    </div>

    <!-- Tickets List -->
    <div v-else-if="tickets.length" class="max-h-48 overflow-y-auto">
      <button
        v-for="ticket in tickets"
        :key="ticket.id"
        class="w-full px-3 py-2 text-left hover:bg-n-alpha-1 transition-colors flex items-start gap-2 border-b border-n-weak last:border-b-0"
        :disabled="isLinking"
        @click="handleTicketSelect(ticket)"
      >
        <div class="flex-1 min-w-0">
          <div class="flex items-center gap-2">
            <span class="text-xs font-mono text-n-slate-11">
              {{ ticket.folio || `#${ticket.display_id}` }}
            </span>
            <span
              class="px-1.5 py-0.5 text-xs rounded"
              :class="getStatusColor(ticket.status)"
            >
              {{ ticket.status }}
            </span>
          </div>
          <p class="text-sm text-n-slate-12 truncate">
            {{ ticket.title }}
          </p>
        </div>
        <Icon
          v-if="isLinking"
          icon="i-lucide-loader-2"
          class="size-4 text-n-slate-11 animate-spin"
        />
      </button>
    </div>

    <!-- Empty State -->
    <div v-else class="px-3 py-4 text-center">
      <Icon icon="i-lucide-ticket" class="size-6 text-n-slate-10 mx-auto mb-2" />
      <p class="text-sm text-n-slate-10">
        {{ $t('TICKETS.ATTACHMENTS.NO_TICKETS_FOR_CONTACT') }}
      </p>
    </div>
  </div>
  </Teleport>
</template>
